/**
 * Nav dismissal (U28).
 *
 * The nav dropdown and the mobile drawer are native <details> elements, which is
 * why they work with no JavaScript at all: keyboard-operable, screen-reader
 * announced, open and close on their own. That is the floor, and it is a good one.
 *
 * But <details> only closes by re-activating its own <summary>. Dawn's custom
 * elements added the behaviours a menu is expected to have, and retiring them took
 * those with it — so a shopper who opens Free Resources, moves the mouse away, and
 * clicks anywhere else finds the panel still hanging open over the page, and a
 * keyboard user pressing Escape finds nothing happens.
 *
 * This restores exactly three: Escape closes, clicking outside closes, and moving
 * focus out closes. It is a progressive enhancement — with this file blocked or
 * broken the nav still opens and closes, because the element does that itself.
 */
(function () {
  const menus = () => document.querySelectorAll('.eg-nav__details[open], .eg-drawer[open]');

  const closeAll = (except) => {
    for (const el of menus()) {
      if (el !== except) el.removeAttribute('open');
    }
  };

  // Escape closes the innermost open menu and returns focus to its summary, so a
  // keyboard user is not dumped at the top of the document.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const open = document.querySelector('.eg-nav__details[open], .eg-drawer[open]');
    if (!open) return;

    open.removeAttribute('open');
    const summary = open.querySelector('summary');
    if (summary) summary.focus();
  });

  // A click outside any open menu closes it — the behaviour every menu on the web
  // has, and whose absence reads as a bug rather than as a missing feature.
  // The dev design panel is exempt: dragging its sliders must not close the
  // very menu being styled.
  document.addEventListener('click', (event) => {
    if (event.target.closest && event.target.closest('#eg-design-panel')) return;
    for (const el of menus()) {
      if (!el.contains(event.target)) el.removeAttribute('open');
    }
  });

  // The desktop dropdown opens on HOVER (Kyle, 2026-07-24). Pointer devices
  // only — on touch, (hover: hover) fails and the summary keeps its native
  // tap-to-toggle. Click and keyboard still work everywhere: this is an
  // enhancement layered on the <details>, not a replacement for it.
  //
  // The 200ms close grace is load-bearing: the panel floats a dialed offset
  // (--eg-nav-panel-offset) BELOW the summary, so the pointer crosses dead
  // space between them — an instant mouseleave close would snap the panel
  // shut mid-crossing. Re-entering (summary or panel, both inside the
  // <details> subtree) cancels the timer.
  //
  // Bound per-element (not delegated), so it only runs once per node —
  // guarded by data-eg-nav-wired. That guard also makes this safe to re-run:
  // a theme-editor section reload (shopify:section:load, below) replaces the
  // header DOM with fresh nodes that carry no listeners at all, so re-running
  // scoped to the new markup is what re-enables the hover behaviour there —
  // it never double-binds a node that already has it.
  function initDesktopHover(scope) {
    if (!window.matchMedia('(hover: hover)').matches) return;
    const details =
      scope.matches && scope.matches('.eg-nav--desktop .eg-nav__details')
        ? [scope]
        : scope.querySelectorAll('.eg-nav--desktop .eg-nav__details');
    for (const det of details) {
      if (det.dataset.egNavWired) continue;
      det.dataset.egNavWired = 'true';
      let closeTimer;
      det.addEventListener('mouseenter', () => {
        clearTimeout(closeTimer);
        det.setAttribute('open', '');
      });
      det.addEventListener('mouseleave', () => {
        closeTimer = setTimeout(() => det.removeAttribute('open'), 200);
      });
    }
  }

  initDesktopHover(document);

  // Theme-editor only: a section reload swaps in a fresh header subtree whose
  // nodes never ran the wiring above. Re-run it scoped to the reloaded
  // section — normal shopper page loads never fire this event, so their
  // behaviour is unchanged (mirrors animations.js's own designMode gate).
  if (Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const target = event.target;
      const hasNavMarkup =
        (target.matches && target.matches('.eg-nav--desktop .eg-nav__details')) ||
        (target.querySelector && target.querySelector('.eg-nav--desktop .eg-nav__details'));
      if (!hasNavMarkup) return;
      initDesktopHover(target);
    });
  }

  // Scrolling CLOSES open menus outright (Kyle, 2026-07-23) — not just fades
  // them. A menu that merely faded was still [open], so the bar could scroll
  // away leaving an open-but-invisible panel, and scrolling back rendered it
  // again. The 2px allowance ignores sub-scroll jitter (rubber-banding,
  // trackpad noise) without ever letting a real scroll slip through.
  //
  // The handler runs on every scroll tick, so it is gated on a boolean kept
  // current by the menus' own native `toggle` events (capture: toggle does
  // not bubble) — the common no-menu-open scroll does zero DOM queries.
  let anyMenuOpen = false;
  document.addEventListener('toggle', () => (anyMenuOpen = menus().length > 0), true);

  let lastScrollY = window.scrollY;
  window.addEventListener(
    'scroll',
    () => {
      if (anyMenuOpen && Math.abs(window.scrollY - lastScrollY) > 2) closeAll();
      lastScrollY = window.scrollY;
    },
    { passive: true }
  );

  // Tabbing out of a menu closes it. Without this, a keyboard user tabs past the
  // last item and the panel stays open behind them, obscuring what they land on.
  document.addEventListener('focusout', (event) => {
    // Focus landing in the design panel is the same exemption as clicks there.
    if (event.relatedTarget && event.relatedTarget.closest && event.relatedTarget.closest('#eg-design-panel')) return;
    for (const el of menus()) {
      // relatedTarget is null when focus leaves the document entirely (e.g. to the
      // browser chrome). Leave the menu alone in that case — the user has not gone
      // anywhere on the page.
      if (event.relatedTarget && !el.contains(event.relatedTarget)) {
        el.removeAttribute('open');
      }
    }
  });
})();
